import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Star, Clock, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GigDetail = () => {
  const { gigId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [gig, setGig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGig();
  }, [gigId]);

  const fetchGig = async () => {
    try {
      const res = await axios.get(`${API}/gigs/${gigId}`);
      setGig(res.data);
    } catch (error) {
      console.error('Failed to fetch gig', error);
      toast.error('Gig not found');
      navigate('/gigs');
    } finally {
      setLoading(false);
    }
  };

  const handleOrder = async () => {
    try {
      const contract = {
        type: 'gig',
        gig_id: gig.id,
        freelancer_id: gig.freelancer_id,
        amount: gig.price,
        delivery_days: gig.delivery_days,
      };
      const res = await axios.post(`${API}/contracts`, contract);
      toast.success('Order placed! Proceed to payment.');
      navigate(`/contracts/${res.data.id}`);
    } catch (error) {
      toast.error('Failed to place order');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-lg">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navigation />
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        <Button variant="ghost" onClick={() => navigate('/gigs')} className="mb-6" data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Gigs
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-3xl font-heading" data-testid="gig-title">{gig.title}</CardTitle>
                <CardDescription>Category: {gig.category}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-lg font-medium">
                      {gig.freelancer_name?.charAt(0)}
                    </div>
                    <div>
                      <div className="font-heading font-semibold">{gig.freelancer_name}</div>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
                        <span>{gig.rating.toFixed(1)} ({gig.reviews_count} reviews)</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="font-heading font-semibold text-lg mb-3">About This Gig</h3>
                  <p className="text-muted-foreground leading-relaxed" data-testid="gig-description">{gig.description}</p>
                </div>

                {gig.tags.length > 0 && (
                  <div>
                    <h3 className="font-heading font-semibold text-lg mb-3">Tags</h3>
                    <div className="flex gap-2 flex-wrap">
                      {gig.tags.map((tag, idx) => (
                        <Badge key={idx} variant="secondary">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div>
            <Card className="sticky top-24">
              <CardContent className="pt-6">
                <div className="text-3xl font-heading font-bold text-secondary mb-6" data-testid="gig-price">
                  ${gig.price}
                </div>

                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
                  <Clock className="w-4 h-4" />
                  <span>{gig.delivery_days} day delivery</span>
                </div>

                {user?.role !== 'freelancer' && user?.id !== gig.freelancer_id && (
                  <Button
                    onClick={handleOrder}
                    className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 h-12"
                    data-testid="order-button"
                  >
                    Order Now
                  </Button>
                )}

                {user?.id === gig.freelancer_id && (
                  <p className="text-center text-sm text-muted-foreground" data-testid="own-gig-message">
                    This is your gig
                  </p>
                )}

                <div className="mt-6 pt-6 border-t">
                  <div className="text-sm text-muted-foreground mb-2">Orders in Queue</div>
                  <div className="text-lg font-heading font-semibold">{gig.orders_count}</div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GigDetail;
